import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { 
  FileText, 
  CheckCircle, 
  AlertTriangle, 
  XCircle,
  TrendingUp,
  BookOpen,
  Code,
  FileQuestion,
  Zap,
  RefreshCw
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useSupabaseAuth } from '@/hooks/auth';
import { EnhancementOperationsMonitor } from '@/components/admin/EnhancementOperationsMonitor';
import { ActiveEnhancementMonitor } from '@/components/admin/ActiveEnhancementMonitor';
import { EnhancementStatsWidget } from '@/components/admin/EnhancementStatsWidget';
import { contentEnhancementTracker } from '@/services/contentEnhancementTracker';
import MainLayout from '@/components/MainLayout';
import { toast } from 'sonner';

interface ContentQualityMetrics {
  course_id: string;
  total_modules: number;
  too_short: number;
  minimal: number;
  adequate: number;
  comprehensive: number;
  avg_content_length: number;
  avg_word_count: number;
  modules_with_practice: number;
  modules_with_quiz: number;
}

interface ModuleInfo {
  module_id: string;
  course_id: string;
  content_length: number;
  content_word_count: number;
  has_practice: boolean;
  has_quiz: boolean;
  language: string;
}

const ContentQualityDashboard: React.FC = () => {
  const { user, hasAccess } = useSupabaseAuth();
  const [metrics, setMetrics] = useState<ContentQualityMetrics[]>([]);
  const [shortModules, setShortModules] = useState<ModuleInfo[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState<string | null>(null);

  useEffect(() => {
    if (user && hasAccess('admin')) {
      loadContentQuality();
    }
  }, [user, hasAccess]);

  const loadContentQuality = async () => {
    setIsLoading(true);
    try {
      // Get real-time quality metrics directly from module_content
      const { data: modulesData, error: modulesError } = await supabase
        .from('module_content')
        .select('course_id, content_word_count, has_practice, has_quiz')
        .not('content', 'is', null);

      if (modulesError) throw modulesError;

      // Calculate metrics per course in JavaScript
      const courseMetrics = new Map<string, ContentQualityMetrics>();
      
      modulesData?.forEach(module => {
        const courseId = module.course_id;
        const wordCount = module.content_word_count || 0;
        
        if (!courseMetrics.has(courseId)) {
          courseMetrics.set(courseId, {
            course_id: courseId,
            total_modules: 0,
            too_short: 0,
            minimal: 0,
            adequate: 0,
            comprehensive: 0,
            avg_content_length: 0,
            avg_word_count: 0,
            modules_with_practice: 0,
            modules_with_quiz: 0
          });
        }
        
        const metrics = courseMetrics.get(courseId)!;
        metrics.total_modules++;
        
        // Categorize by word count (matching real thresholds)
        if (wordCount < 300) metrics.too_short++;
        else if (wordCount < 800) metrics.minimal++;
        else if (wordCount < 1500) metrics.adequate++;
        else metrics.comprehensive++;
        
        metrics.avg_word_count += wordCount;
        if (module.has_practice) metrics.modules_with_practice++;
        if (module.has_quiz) metrics.modules_with_quiz++;
      });

      // Calculate averages and convert to array
      const metricsArray = Array.from(courseMetrics.values()).map(m => ({
        ...m,
        avg_word_count: Math.round(m.avg_word_count / m.total_modules)
      }));

      setMetrics(metricsArray.sort((a, b) => b.too_short - a.too_short));

      // Get modules that need enhancement (<300 words)
      const { data: shortModulesData, error: shortModulesError } = await supabase
        .from('module_content')
        .select('module_id, course_id, content, has_practice, has_quiz, language, content_word_count')
        .lt('content_word_count', 300)
        .order('content_word_count', { ascending: true })
        .limit(50);

      if (shortModulesError) throw shortModulesError;
      
      const formattedModules = shortModulesData?.map(m => ({
        module_id: m.module_id,
        course_id: m.course_id,
        content_length: m.content?.length || 0,
        content_word_count: m.content_word_count || 0,
        has_practice: m.has_practice || false,
        has_quiz: m.has_quiz || false,
        language: m.language || 'javascript'
      })) || [];

      setShortModules(formattedModules);
    } catch (error) {
      console.error('Error loading content quality:', error);
      toast.error('Failed to load content quality metrics');
    } finally {
      setIsLoading(false);
    }
  };

  const refreshMetrics = async () => {
    toast.info('Refreshing content quality metrics...');
    
    try {
      // Reload data from database
      await loadContentQuality();
      toast.success('Metrics refreshed successfully');
    } catch (error) {
      console.error('Error refreshing metrics:', error);
      toast.error('Failed to refresh metrics');
    }
  };

  const handleBulkEnhancement = async () => {
    if (isEnhancing) return;
    
    const modulesToEnhance = shortModules.slice(0, 20); // Process 20 at a time
    
    if (modulesToEnhance.length === 0) {
      toast.error('No modules to enhance');
      return;
    }

    const confirmed = confirm(
      `This will enhance ${modulesToEnhance.length} modules using AI.\n\n` +
      `Estimated time: ${Math.ceil(modulesToEnhance.length / 2)} minutes\n` +
      `This will use Lovable AI credits.\n\n` +
      `Continue?`
    );

    if (!confirmed) return;

    setIsEnhancing(true);
    let successCount = 0;
    let failCount = 0;

    // Create operation tracking
    const operationId = await contentEnhancementTracker.createOperation(
      'bulk_enhance',
      modulesToEnhance.length,
      { batch_size: 20, target_word_count: 2000 }
    );

    if (!operationId) {
      toast.error('Failed to create operation tracking');
      setIsEnhancing(false);
      return;
    }

    toast.info(`Starting enhancement of ${modulesToEnhance.length} modules...`);

    for (let i = 0; i < modulesToEnhance.length; i++) {
      const module = modulesToEnhance[i];
      
      // Create log entry for this module
      const logId = await contentEnhancementTracker.createLog(
        operationId,
        module.module_id,
        module.course_id,
        'content',
        module.content_word_count
      );
      
      try {
        // Get full module data
        const { data: moduleData, error: fetchError } = await supabase
          .from('module_content')
          .select('content, module_id, course_id')
          .eq('module_id', module.module_id)
          .eq('course_id', module.course_id)
          .single();

        if (fetchError) throw fetchError;

        toast.info(`Enhancing ${i + 1}/${modulesToEnhance.length}: ${module.module_id}`);

        // Call enhancement function with tracking
        const { data: enhancedData, error: enhanceError } = await supabase.functions.invoke(
          'enhance-module-content',
          {
            body: {
              moduleId: module.module_id,
              courseId: module.course_id,
              currentContent: moduleData.content || '',
              moduleTitle: module.module_id,
              targetWordCount: 2000,
              operationId,
              logId
            }
          }
        );

        if (enhanceError) throw enhanceError;

        // Update module with enhanced content
        const { error: updateError } = await supabase
          .from('module_content')
          .update({
            content: enhancedData.enhancedContent,
            content_word_count: enhancedData.metrics.wordCount,
            updated_at: new Date().toISOString()
          })
          .eq('module_id', module.module_id)
          .eq('course_id', module.course_id);

        if (updateError) throw updateError;

        successCount++;
        
        // Update operation progress
        await contentEnhancementTracker.updateOperation(operationId, {
          processed_items: i + 1,
          successful_items: successCount,
          failed_items: failCount
        });

        toast.success(
          `✓ ${module.module_id}: ${enhancedData.metrics.originalWordCount} → ${enhancedData.metrics.wordCount} words`
        );

        // Small delay to avoid rate limits
        if (i < modulesToEnhance.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 2000));
        }

      } catch (error) {
        console.error(`Failed to enhance ${module.module_id}:`, error);
        failCount++;
        
        // Update failure in log if logId exists
        if (logId) {
          await contentEnhancementTracker.updateLog(logId, {
            status: 'failed',
            completed_at: new Date().toISOString(),
            error_message: error instanceof Error ? error.message : 'Unknown error'
          });
        }
        
        // Update operation progress
        await contentEnhancementTracker.updateOperation(operationId, {
          processed_items: i + 1,
          successful_items: successCount,
          failed_items: failCount
        });
        
        toast.error(`✗ Failed: ${module.module_id}`);
      }
    }

    // Mark operation as completed
    await contentEnhancementTracker.updateOperation(operationId, {
      status: failCount === modulesToEnhance.length ? 'failed' : 'completed',
      completed_at: new Date().toISOString(),
      processed_items: modulesToEnhance.length,
      successful_items: successCount,
      failed_items: failCount
    });

    setIsEnhancing(false);
    
    toast.success(
      `Enhancement complete!\n✓ Success: ${successCount}\n✗ Failed: ${failCount}`
    );

    // Refresh metrics
    await loadContentQuality();
  };

  const getStatusColor = (count: number, total: number) => {
    const percentage = (count / total) * 100;
    if (percentage > 50) return 'text-red-600 dark:text-red-400';
    if (percentage > 25) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-green-600 dark:text-green-400';
  };

  const getStatusBadge = (wordCount: number) => {
    if (wordCount < 300) return <Badge variant="destructive">Too Short</Badge>;
    if (wordCount < 800) return <Badge className="bg-yellow-500">Minimal</Badge>;
    if (wordCount < 1500) return <Badge className="bg-blue-500">Adequate</Badge>;
    return <Badge className="bg-green-500">Comprehensive</Badge>;
  };

  if (!user || !hasAccess('admin')) {
    return (
      <MainLayout>
        <div className="container mx-auto py-8">
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Access Denied</AlertTitle>
            <AlertDescription>
              You need admin permissions to access the Content Quality Dashboard.
            </AlertDescription>
          </Alert>
        </div>
      </MainLayout>
    );
  }

  const totalModules = metrics.reduce((sum, m) => sum + m.total_modules, 0);
  const totalShort = metrics.reduce((sum, m) => sum + m.too_short, 0);
  const totalMinimal = metrics.reduce((sum, m) => sum + m.minimal, 0);
  const totalComprehensive = metrics.reduce((sum, m) => sum + m.comprehensive, 0);

  return (
    <MainLayout>
      <div className="container mx-auto py-8 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Content Quality Dashboard</h1>
            <p className="text-muted-foreground">
              Monitor and enhance module content quality across all courses
            </p>
          </div>
          <Button onClick={refreshMetrics} disabled={isLoading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Overview Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Total Modules</p>
                  <p className="text-3xl font-bold">{totalModules}</p>
                </div>
                <FileText className="h-8 w-8 text-primary" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Too Short</p>
                  <p className={`text-3xl font-bold ${getStatusColor(totalShort, totalModules)}`}>
                    {totalShort}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {((totalShort / totalModules) * 100).toFixed(1)}%
                  </p>
                </div>
                <XCircle className="h-8 w-8 text-red-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Minimal</p>
                  <p className={`text-3xl font-bold ${getStatusColor(totalMinimal, totalModules)}`}>
                    {totalMinimal}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {((totalMinimal / totalModules) * 100).toFixed(1)}%
                  </p>
                </div>
                <AlertTriangle className="h-8 w-8 text-yellow-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Comprehensive</p>
                  <p className="text-3xl font-bold text-green-600 dark:text-green-400">
                    {totalComprehensive}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {((totalComprehensive / totalModules) * 100).toFixed(1)}%
                  </p>
                </div>
                <CheckCircle className="h-8 w-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <Tabs defaultValue="overview" className="space-y-4">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="courses">By Course</TabsTrigger>
            <TabsTrigger value="modules">Modules Needing Enhancement</TabsTrigger>
            <TabsTrigger value="actions">Bulk Actions</TabsTrigger>
            <TabsTrigger value="operations">Operations Monitor</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Content Quality Distribution</CardTitle>
                <CardDescription>
                  Overview of content quality across all courses
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span>Comprehensive (1500+ words)</span>
                    <span className="font-medium">{totalComprehensive} modules</span>
                  </div>
                  <Progress value={(totalComprehensive / totalModules) * 100} className="h-2" />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span>Adequate (800-1500 words)</span>
                    <span className="font-medium">
                      {metrics.reduce((sum, m) => sum + m.adequate, 0)} modules
                    </span>
                  </div>
                  <Progress 
                    value={(metrics.reduce((sum, m) => sum + m.adequate, 0) / totalModules) * 100} 
                    className="h-2" 
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span>Minimal (300-800 words)</span>
                    <span className="font-medium">{totalMinimal} modules</span>
                  </div>
                  <Progress value={(totalMinimal / totalModules) * 100} className="h-2" />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span>Too Short (&lt;300 words)</span>
                    <span className="font-medium">{totalShort} modules</span>
                  </div>
                  <Progress value={(totalShort / totalModules) * 100} className="h-2" />
                </div>
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Code className="h-5 w-5" />
                    Practice Coverage
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span>Modules with Practice</span>
                      <span className="font-bold">
                        {metrics.reduce((sum, m) => sum + m.modules_with_practice, 0)} / {totalModules}
                      </span>
                    </div>
                    <Progress 
                      value={(metrics.reduce((sum, m) => sum + m.modules_with_practice, 0) / totalModules) * 100} 
                    />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileQuestion className="h-5 w-5" />
                    Quiz Coverage
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span>Modules with Quizzes</span>
                      <span className="font-bold">
                        {metrics.reduce((sum, m) => sum + m.modules_with_quiz, 0)} / {totalModules}
                      </span>
                    </div>
                    <Progress 
                      value={(metrics.reduce((sum, m) => sum + m.modules_with_quiz, 0) / totalModules) * 100} 
                    />
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="courses" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Quality Metrics by Course</CardTitle>
                <CardDescription>
                  Detailed breakdown of content quality for each course
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {metrics.map((metric) => (
                    <div key={metric.course_id} className="border rounded-lg p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-semibold">{metric.course_id}</h3>
                          <p className="text-sm text-muted-foreground">
                            {metric.total_modules} modules • Avg {Math.round(metric.avg_word_count)} words
                          </p>
                        </div>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => setSelectedCourse(metric.course_id)}
                        >
                          View Details
                        </Button>
                      </div>
                      
                      <div className="grid grid-cols-4 gap-2 text-sm">
                        <div className="text-center p-2 bg-red-500/10 rounded">
                          <div className="font-bold text-red-600 dark:text-red-400">
                            {metric.too_short}
                          </div>
                          <div className="text-xs">Too Short</div>
                        </div>
                        <div className="text-center p-2 bg-yellow-500/10 rounded">
                          <div className="font-bold text-yellow-600 dark:text-yellow-400">
                            {metric.minimal}
                          </div>
                          <div className="text-xs">Minimal</div>
                        </div>
                        <div className="text-center p-2 bg-blue-500/10 rounded">
                          <div className="font-bold text-blue-600 dark:text-blue-400">
                            {metric.adequate}
                          </div>
                          <div className="text-xs">Adequate</div>
                        </div>
                        <div className="text-center p-2 bg-green-500/10 rounded">
                          <div className="font-bold text-green-600 dark:text-green-400">
                            {metric.comprehensive}
                          </div>
                          <div className="text-xs">Comprehensive</div>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div className="flex items-center justify-between p-2 bg-muted/50 rounded">
                          <span>Has Practice:</span>
                          <span className="font-medium">{metric.modules_with_practice}</span>
                        </div>
                        <div className="flex items-center justify-between p-2 bg-muted/50 rounded">
                          <span>Has Quiz:</span>
                          <span className="font-medium">{metric.modules_with_quiz}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="modules" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Modules Requiring Enhancement</CardTitle>
                <CardDescription>
                  Top 50 modules with shortest content (sorted by word count)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {shortModules.map((module) => (
                    <div 
                      key={`${module.course_id}-${module.module_id}`}
                      className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <code className="text-sm font-mono bg-muted px-2 py-1 rounded">
                            {module.module_id}
                          </code>
                          {getStatusBadge(module.content_word_count)}
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          Course: {module.course_id} • {module.content_word_count} words • {module.language}
                        </p>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        {module.has_practice && (
                          <Badge variant="outline" className="bg-green-500/10">
                            <Code className="h-3 w-3 mr-1" />
                            Practice
                          </Badge>
                        )}
                        {module.has_quiz && (
                          <Badge variant="outline" className="bg-blue-500/10">
                            <FileQuestion className="h-3 w-3 mr-1" />
                            Quiz
                          </Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="actions" className="space-y-4">
            <Alert>
              <Zap className="h-4 w-4" />
              <AlertTitle>Bulk Enhancement Actions</AlertTitle>
              <AlertDescription>
                These actions will enhance multiple modules at once. Use with caution.
              </AlertDescription>
            </Alert>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card>
                <CardHeader>
                  <CardTitle>Enhance Short Content</CardTitle>
                  <CardDescription>
                    Expand modules with less than 600 words to comprehensive content using AI
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="text-sm space-y-2">
                    <p>✓ Will enhance {totalShort + totalMinimal} modules</p>
                    <p>✓ Target: 1000-3500 words per module</p>
                    <p>✓ Adds code examples and best practices</p>
                    <p>✓ Uses Lovable AI (google/gemini-2.5-flash)</p>
                  </div>
                  <Button 
                    className="w-full" 
                    disabled={isEnhancing}
                    onClick={handleBulkEnhancement}
                  >
                    {isEnhancing ? (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        Enhancing...
                      </>
                    ) : (
                      <>
                        <TrendingUp className="h-4 w-4 mr-2" />
                        Enhance Short Content
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Generate Missing Practice</CardTitle>
                  <CardDescription>
                    Create practice exercises for modules without them
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="text-sm space-y-2">
                    <p>✓ Will generate {totalModules - metrics.reduce((sum, m) => sum + m.modules_with_practice, 0)} exercises</p>
                    <p>✓ Includes starter code and solution</p>
                    <p>✓ 3-5 progressive hints per exercise</p>
                  </div>
                  <Button 
                    className="w-full" 
                    variant="outline"
                    disabled={isEnhancing}
                    onClick={() => {
                      toast.info('Practice generation coming soon! This will create comprehensive exercises.');
                    }}
                  >
                    <Code className="h-4 w-4 mr-2" />
                    Generate Practice Labs
                  </Button>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Generate Missing Quizzes</CardTitle>
                  <CardDescription>
                    Create comprehensive quizzes for modules without them
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="text-sm space-y-2">
                    <p>✓ Will generate {totalModules - metrics.reduce((sum, m) => sum + m.modules_with_quiz, 0)} quizzes</p>
                    <p>✓ 10-15 questions per quiz</p>
                    <p>✓ Mixed difficulty levels</p>
                  </div>
                  <Button 
                    className="w-full" 
                    variant="outline"
                    disabled={isEnhancing}
                    onClick={() => {
                      toast.info('Quiz generation coming soon! This will create comprehensive assessments.');
                    }}
                  >
                    <FileQuestion className="h-4 w-4 mr-2" />
                    Generate Quizzes
                  </Button>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Validate All Content</CardTitle>
                  <CardDescription>
                    Run quality checks on all modules
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="text-sm space-y-2">
                    <p>✓ Check content length</p>
                    <p>✓ Validate code examples</p>
                    <p>✓ Check for broken links</p>
                  </div>
                  <Button 
                    className="w-full" 
                    variant="outline"
                    onClick={() => {
                      toast.info('Content validation starting...');
                    }}
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Run Validation
                  </Button>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="operations" className="space-y-4">
            <EnhancementStatsWidget />
            <ActiveEnhancementMonitor />
            <EnhancementOperationsMonitor />
          </TabsContent>
        </Tabs>
      </div>
    </MainLayout>
  );
};

export default ContentQualityDashboard;
